import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faMagic, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const POSITION_COLORS = [
  { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', hex: '#f97316' },
  { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', hex: '#22c55e' },
  { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400', hex: '#3b82f6' },
  { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-400', hex: '#a855f7' },
  { bg: 'bg-pink-500/20', border: 'border-pink-500', text: 'text-pink-400', hex: '#ec4899' },
  { bg: 'bg-cyan-500/20', border: 'border-cyan-500', text: 'text-cyan-400', hex: '#06b6d4' },
];

const ATTACK_MODE_INFO = {
  sniper: { label: 'Sniper', desc: 'Uses a single payload set. Iterates through payloads, inserting each into one position at a time while others keep original values.' },
  battering_ram: { label: 'Battering Ram', desc: 'Uses a single payload set. Each payload is placed into all positions simultaneously.' },
  pitchfork: { label: 'Pitchfork', desc: 'Uses one payload set per position. Iterates all sets in parallel (position 1 gets payload 1 from set 1, position 2 gets payload 1 from set 2, etc.).' },
  cluster_bomb: { label: 'Cluster Bomb', desc: 'Uses one payload set per position. Tests all combinations (cartesian product) of payloads across all positions.' },
};

// Use \n only — browsers normalize \r\n to \n in textareas, which causes selection offset bugs
const buildRawRequest = (method, url, headers, body) => {
  let raw = '';
  try {
    const parsed = new URL(url);
    const path = parsed.pathname + parsed.search;
    raw += `${method} ${path} HTTP/1.1\n`;
    raw += `Host: ${parsed.host}\n`;
  } catch {
    raw += `${method} ${url || '/'} HTTP/1.1\n`;
    raw += `Host: \n`;
  }
  (headers || []).forEach(h => {
    if (h.name?.toLowerCase() !== 'host') {
      raw += `${h.name}: ${h.value}\n`;
    }
  });
  raw += '\n';
  if (body) raw += body;
  return raw;
};

const parseRawRequest = (raw) => {
  const lines = raw.split('\n');
  const requestLine = lines[0] || '';
  const parts = requestLine.split(' ');
  const method = parts[0] || 'GET';
  const pathAndQuery = parts[1] || '/';

  let headers = [];
  let bodyStart = -1;
  let host = '';

  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === '') {
      bodyStart = i + 1;
      break;
    }
    const colonIdx = lines[i].indexOf(':');
    if (colonIdx > 0) {
      const name = lines[i].slice(0, colonIdx).trim();
      const value = lines[i].slice(colonIdx + 1).trim();
      headers.push({ name, value });
      if (name.toLowerCase() === 'host') host = value;
    }
  }

  const body = bodyStart > 0 ? lines.slice(bodyStart).join('\n') : '';
  const protocol = 'https';
  const url = `${protocol}://${host}${pathAndQuery}`;

  return { method, url, headers, body };
};

// Map frontend location names to backend enum values
const LOCATION_MAP = {
  query_value: 'query_value',
  body: 'body',
  header_value: 'header_value',
  cookie_value: 'cookie_value',
  path: 'path',
};

const autoDetectPositions = (rawText) => {
  const positions = [];
  const parsed = parseRawRequest(rawText);

  // Detect query params
  try {
    const url = new URL(parsed.url);
    url.searchParams.forEach((value, key) => {
      const searchStr = `${key}=${value}`;
      const idx = rawText.indexOf(searchStr);
      if (idx >= 0) {
        const valueStart = idx + key.length + 1;
        positions.push({
          startIndex: valueStart,
          endIndex: valueStart + value.length,
          originalValue: value,
          location: 'query_value',
          parameterName: key,
        });
      }
    });
  } catch {}

  // Detect body params (form-encoded)
  if (parsed.body && parsed.body.includes('=')) {
    const blankLineIdx = rawText.indexOf('\n\n');
    const bodyOffset = blankLineIdx >= 0 ? blankLineIdx + 2 : 0;
    if (bodyOffset > 0) {
      parsed.body.split('&').forEach(pair => {
        const eqIdx = pair.indexOf('=');
        if (eqIdx > 0) {
          const key = pair.slice(0, eqIdx);
          const value = pair.slice(eqIdx + 1);
          const searchStr = `${key}=${value}`;
          const idx = rawText.indexOf(searchStr, bodyOffset);
          if (idx >= 0) {
            const valueStart = idx + key.length + 1;
            positions.push({
              startIndex: valueStart,
              endIndex: valueStart + value.length,
              originalValue: value,
              location: 'body',
              parameterName: key,
            });
          }
        }
      });
    }
  }

  // Detect Cookie values
  const cookieMatch = rawText.match(/Cookie:\s*(.+)/i);
  if (cookieMatch) {
    const cookieLineStart = rawText.indexOf(cookieMatch[0]);
    const cookieHeaderStart = cookieLineStart + cookieMatch[0].indexOf(cookieMatch[1]);
    cookieMatch[1].split(';').forEach(cookie => {
      const trimmed = cookie.trim();
      const eqIdx = trimmed.indexOf('=');
      if (eqIdx > 0) {
        const key = trimmed.slice(0, eqIdx);
        const value = trimmed.slice(eqIdx + 1);
        const searchStr = `${key}=${value}`;
        const idx = rawText.indexOf(searchStr, cookieHeaderStart);
        if (idx >= 0) {
          const valueStart = idx + key.length + 1;
          positions.push({
            startIndex: valueStart,
            endIndex: valueStart + value.length,
            originalValue: value,
            location: 'cookie_value',
            parameterName: key,
          });
        }
      }
    });
  }

  return positions;
};

const IntruderPositions = ({ attackMode, onAttackModeChange, method, url, headers, body, positions, onPositionsChange, onRequestChange, isDarkMode }) => {
  const [rawText, setRawText] = useState('');
  const textareaRef = useRef(null);

  // Build raw text from request parts
  useEffect(() => {
    const raw = buildRawRequest(method, url, headers, body);
    setRawText(raw);
  }, [method, url, headers, body]);

  // Convert positions to § markers for display
  const getDisplayText = useCallback(() => {
    if (positions.length === 0) return rawText;

    // Sort by startIndex descending so we can insert markers without shifting indices
    const sorted = [...positions].sort((a, b) => b.startIndex - a.startIndex);
    let display = rawText;
    sorted.forEach(pos => {
      if (pos.startIndex >= 0 && pos.endIndex <= display.length) {
        display = display.slice(0, pos.endIndex) + '§' + display.slice(pos.endIndex);
        display = display.slice(0, pos.startIndex) + '§' + display.slice(pos.startIndex);
      }
    });
    return display;
  }, [rawText, positions]);

  const handleAddPosition = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const selStart = textarea.selectionStart;
    const selEnd = textarea.selectionEnd;

    if (selStart === selEnd) return; // No selection

    const displayText = getDisplayText();

    // Map display indices to raw text indices by counting § markers
    let markersBeforeStart = 0;
    for (let i = 0; i < selStart && i < displayText.length; i++) {
      if (displayText[i] === '§') markersBeforeStart++;
    }
    const rawStart = selStart - markersBeforeStart;

    let markersBeforeEnd = 0;
    for (let i = 0; i < selEnd && i < displayText.length; i++) {
      if (displayText[i] === '§') markersBeforeEnd++;
    }
    const rawEnd = selEnd - markersBeforeEnd;

    // Clamp to valid range
    const clampedStart = Math.max(0, Math.min(rawStart, rawText.length));
    const clampedEnd = Math.max(clampedStart, Math.min(rawEnd, rawText.length));

    if (clampedStart === clampedEnd) return;

    const selectedValue = rawText.slice(clampedStart, clampedEnd);

    // Infer location from position in raw request
    let location = 'query_value';
    const blankLineIdx = rawText.indexOf('\n\n');

    if (clampedStart <= rawText.indexOf('\n')) {
      // On request line
      location = 'path';
    } else if (blankLineIdx >= 0 && clampedStart > blankLineIdx + 1) {
      // After blank line = body
      location = 'body';
    } else {
      // In headers
      const lineStart = rawText.lastIndexOf('\n', clampedStart - 1) + 1;
      const headerLine = rawText.slice(lineStart, rawText.indexOf('\n', lineStart));
      if (headerLine.toLowerCase().startsWith('cookie:')) {
        location = 'cookie_value';
      } else {
        location = 'header_value';
      }
    }

    // Check if on the request line query string
    const firstLine = rawText.slice(0, rawText.indexOf('\n'));
    if (clampedStart < firstLine.length && firstLine.includes('?')) {
      location = 'query_value';
    }

    const newPos = {
      id: `ip${Date.now()}`,
      startIndex: clampedStart,
      endIndex: clampedEnd,
      originalValue: selectedValue,
      location,
      parameterName: '',
    };

    onPositionsChange([...positions, newPos]);
  };

  const handleClearPositions = () => {
    onPositionsChange([]);
  };

  const handleAutoDetect = () => {
    const detected = autoDetectPositions(rawText);
    const newPositions = detected.map((p, i) => ({
      ...p,
      id: `ip${Date.now()}_${i}`,
    }));
    onPositionsChange(newPositions);
  };

  const handleRawTextChange = (e) => {
    const newDisplay = e.target.value;
    // Strip § markers to get raw text
    const stripped = newDisplay.replace(/§/g, '');
    setRawText(stripped);

    // Parse back to request components
    const parsed = parseRawRequest(stripped);
    onRequestChange(parsed);

    // Clear positions since text changed
    if (positions.length > 0) {
      onPositionsChange([]);
    }
  };

  const displayText = getDisplayText();

  // Build highlighted preview
  const renderHighlightedPreview = () => {
    if (positions.length === 0) return null;

    const sorted = [...positions].sort((a, b) => a.startIndex - b.startIndex);
    const parts = [];
    let lastEnd = 0;

    sorted.forEach((pos, i) => {
      const colorIdx = i % POSITION_COLORS.length;
      const color = POSITION_COLORS[colorIdx];

      if (pos.startIndex > lastEnd) {
        parts.push(
          <span key={`text-${i}`} className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
            {rawText.slice(lastEnd, pos.startIndex)}
          </span>
        );
      }

      parts.push(
        <span
          key={`pos-${i}`}
          className={`${color.bg} border-b-2 ${color.border} rounded-sm px-0.5`}
          title={`Position ${i + 1}: ${pos.parameterName || pos.originalValue}`}
        >
          <span className={`text-[9px] font-bold ${color.text}`}>§</span>
          {rawText.slice(pos.startIndex, pos.endIndex)}
          <span className={`text-[9px] font-bold ${color.text}`}>§</span>
        </span>
      );
      lastEnd = pos.endIndex;
    });

    if (lastEnd < rawText.length) {
      parts.push(
        <span key="text-end" className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
          {rawText.slice(lastEnd)}
        </span>
      );
    }

    return (
      <div className={`font-mono text-xs whitespace-pre-wrap p-3 rounded border overflow-auto max-h-48 ${
        isDarkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        {parts}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Attack mode selector */}
      <div>
        <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Attack Type
        </label>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(ATTACK_MODE_INFO).map(([mode, info]) => (
            <button
              key={mode}
              onClick={() => onAttackModeChange(mode)}
              className={`p-2 rounded border text-left transition-colors ${
                attackMode === mode
                  ? (isDarkMode ? 'bg-orange-900/30 border-orange-500 text-orange-300' : 'bg-orange-50 border-orange-400 text-orange-700')
                  : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300')
              }`}
            >
              <div className="text-xs font-medium">{info.label}</div>
              <div className="text-[10px] mt-0.5 opacity-70 line-clamp-2">{info.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Position summary */}
      <div className={`flex items-center justify-between px-3 py-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {positions.length} position{positions.length !== 1 ? 's' : ''} defined
          </span>
          {positions.length > 0 && (
            <div className="flex gap-1">
              {positions.map((pos, i) => (
                <span
                  key={pos.id || i}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${POSITION_COLORS[i % POSITION_COLORS.length].bg} ${POSITION_COLORS[i % POSITION_COLORS.length].text}`}
                >
                  {pos.parameterName || pos.originalValue?.slice(0, 12) || `pos${i + 1}`}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleAddPosition}
            className="px-2.5 py-1 rounded text-[10px] font-medium bg-orange-600 text-white hover:bg-orange-700"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />Add §
          </button>
          <button
            onClick={handleAutoDetect}
            className={`px-2.5 py-1 rounded text-[10px] font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            <FontAwesomeIcon icon={faMagic} className="mr-1" />Auto §
          </button>
          <button
            onClick={handleClearPositions}
            className={`px-2.5 py-1 rounded text-[10px] font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            <FontAwesomeIcon icon={faTimes} className="mr-1" />Clear §
          </button>
        </div>
      </div>

      {/* Raw request editor */}
      <div>
        <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Raw Request
          <span className="font-normal ml-2 opacity-60">Select text and click "Add §" to mark insertion points</span>
        </label>
        <textarea
          ref={textareaRef}
          value={displayText}
          onChange={handleRawTextChange}
          rows={14}
          spellCheck={false}
          className={`w-full font-mono text-xs rounded border px-3 py-2 resize-y ${
            isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
          }`}
        />
      </div>

      {/* Highlighted preview */}
      {positions.length > 0 && (
        <div>
          <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Position Preview
          </label>
          {renderHighlightedPreview()}
        </div>
      )}

      {/* Info box */}
      <div className={`flex items-start gap-2 p-2.5 rounded text-[10px] ${isDarkMode ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
        <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0" />
        <span>
          Mark positions with § delimiters to define where payloads will be inserted.
          <strong> Auto §</strong> detects query params, body params, and cookie values.
          For <strong>{ATTACK_MODE_INFO[attackMode]?.label}</strong>: {ATTACK_MODE_INFO[attackMode]?.desc}
        </span>
      </div>
    </div>
  );
};

export default IntruderPositions;
